// hooks/useFiscal.js
// Hook central para acceder al contexto fiscal del tenant.
// Evita hardcodear 'ITBIS', 'RNC', 'NCF' en los componentes.
// El contexto viene del endpoint /api/configuracion (campo `fiscal`).

import { useState, useEffect } from 'react';
import api from '../services/api';

// Valores por defecto (República Dominicana) para backward-compatibility
const DEFAULTS_DO = {
  pais_codigo: 'DO',
  pais_nombre: 'República Dominicana',
  impuesto_label: 'ITBIS',
  impuesto_tasa: 18,
  impuesto_tasas_adicionales: [],
  id_fiscal_empresa_label: 'RNC',
  id_fiscal_empresa_mask: '999-99999-9',
  id_fiscal_persona_label: 'Cédula',
  sistema_comprobantes: 'NCF',
  tiene_comprobantes: true,
  tipos_comprobantes: {},
  reportes_fiscales: ['606', '607', '608'],
  ente_regulador: 'DGII',
  ente_regulador_url: 'https://dgii.gov.do',
  moneda: { codigo: 'DOP', simbolo: 'RD$', decimales: 2, nombre: 'Peso Dominicano' },
  monedas_aceptadas: ['DOP', 'USD', 'EUR'],
  zona_horaria: 'America/Santo_Domingo',
  formato_fecha: 'DD/MM/YYYY',
  separador_decimal: '.',
  separador_miles: ','
};

// Cache en memoria para evitar múltiples llamadas al API en la misma sesión
let cachedFiscal = null;

/**
 * useFiscal — Hook para obtener el contexto fiscal del tenant actual.
 *
 * Uso básico:
 *   const { impuesto_label, id_fiscal_empresa_label } = useFiscal();
 *   // Muestra "ITBIS" en RD, "IVA" en Colombia/México, etc.
 *   // Muestra "RNC" en RD, "NIT" en Colombia, "RFC" en México, etc.
 *
 * Uso avanzado:
 *   const fiscal = useFiscal();
 *   fiscal.moneda.simbolo   // 'RD$', 'COP$', 'MX$'
 *   fiscal.tiene_comprobantes  // true/false según país
 *   fiscal.reportes_fiscales   // ['606','607'] en RD, ['IVA_bimestral'] en CO
 */
export const useFiscal = () => {
  const [fiscal, setFiscal] = useState(cachedFiscal || DEFAULTS_DO);

  useEffect(() => {
    if (cachedFiscal) return; // Ya tenemos el contexto en caché

    const cargar = async () => {
      try {
        const res = await api.get('/configuracion');
        const ctx = res.data?.fiscal;
        if (ctx) {
          cachedFiscal = ctx;
          setFiscal(ctx);
        }
      } catch {
        // Si falla, mantener los defaults (RD) — no rompe nada existente
      }
    };

    cargar();
  }, []);

  return fiscal;
};

/**
 * Limpia el caché del contexto fiscal.
 * Llamar cuando el usuario cambia de país o cierra sesión.
 */
export const clearFiscalCache = () => {
  cachedFiscal = null;
};

export default useFiscal;
